import React, { useState, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import cx from "classnames";
import AttachFile from "@material-ui/icons/AttachFile";
import Close from "@material-ui/icons/Close";
import { ThreeDots } from "react-loader-spinner";
import { EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useTranslation } from "react-i18next";
import { useFeedback } from "./FeedbackContext";
import { topicFormStyles, editorToolbarConfig } from "./feedbackStyles";
import useThemeColors from "../../hooks/useThemeColors";

const useStyles = makeStyles(topicFormStyles);

let _attachId = 800;

export default function FeedbackTopicForm({ clientColor }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientTextColor } = useThemeColors();
  const { addTopic, setCreatingTopic } = useFeedback();
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (editorRef.current) editorRef.current.focusEditor();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const hasMessage = useCallback(() => {
    return editorState.getCurrentContent().getPlainText().trim().length > 0;
  }, [editorState]);

  const getHtml = useCallback(
    () => draftToHtml(convertToRaw(editorState.getCurrentContent())),
    [editorState],
  );

  const handleSubmit = async () => {
    if (!hasMessage() || submitting) return;
    setSubmitting(true);
    try {
      await addTopic(
        getHtml(),
        attachments.map((a) => a.file),
      );
    } catch (err) {
      console.error("[Feedback] Failed to create topic", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((f) => ({
      id: `attach-${++_attachId}`,
      name: f.name,
      size: f.size,
      file: f,
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
    e.target.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={classes.formContainer}>
      <div className={classes.formTitle}>{t("feedback.new_topic")}</div>

      <div className={classes.fieldGroup}>
        <label className={classes.label}>
          {t("feedback.describe_adjustment")}
        </label>
        <div className={classes.editorWrapper}>
          <Editor
            ref={editorRef}
            editorState={editorState}
            onEditorStateChange={setEditorState}
            toolbar={editorToolbarConfig}
            placeholder={t("feedback.describe_placeholder")}
          />
        </div>
      </div>

      {attachments.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: "5px",
            flexWrap: "wrap",
            marginBottom: "6px",
          }}
        >
          {attachments.map((a) => (
            <span
              key={a.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "10px",
                color: "#555",
                padding: "2px 7px",
                borderRadius: "4px",
                backgroundColor: "#f5f5f5",
              }}
            >
              {a.name} ({formatSize(a.size)})
              <Close
                style={{ fontSize: "12px", cursor: "pointer", color: "#999" }}
                onClick={() => removeAttachment(a.id)}
              />
            </span>
          ))}
        </div>
      )}

      <div className={classes.formActions}>
        <button
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "11px",
            color: "#888",
            cursor: "pointer",
            padding: "4px 8px",
            borderRadius: "4px",
            border: "1px solid #e4e4e4",
            background: "transparent",
            fontFamily: "inherit",
            marginRight: "auto",
          }}
          onClick={() => fileInputRef.current?.click()}
          type="button"
        >
          <AttachFile style={{ fontSize: "13px" }} />
          {t("feedback.attach")}
        </button>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        <button
          className={cx(classes.formBtn, classes.cancelBtn)}
          onClick={() => setCreatingTopic(false)}
        >
          {t("cancel")}
        </button>
        <button
          className={cx(classes.formBtn, classes.submitBtn)}
          onClick={handleSubmit}
          disabled={!hasMessage() || submitting}
          style={
            hasMessage() && !submitting && clientColor
              ? { background: clientColor }
              : undefined
          }
        >
          {submitting ? (
            <ThreeDots
              height={12}
              width={20}
              color={clientTextColor}
              ariaLabel="creating"
            />
          ) : (
            t("feedback.create")
          )}
        </button>
      </div>
    </div>
  );
}

FeedbackTopicForm.propTypes = {
  clientColor: PropTypes.string,
};
